/**
 * ElevenLabs Conversational-AI SDK adapter.
 *
 * Wraps `@elevenlabs/react`'s `useConversation` with our voice-session
 * state so the SDK auto-starts as soon as Phase 1's session-start handler
 * has handed us back an `agentId`.
 *
 *   - `onError` → flips the voice store into the 'error' state so the call
 *     controls can surface a banner. Error payloads are stringified
 *     defensively (the SDK's onError takes a generic `Error | unknown`).
 *
 * Note (voice-as-chat-channel cutover): the SDK no longer feeds transcript
 * bubbles into a local store. Voice turns are persisted + broadcast by the
 * backend as regular `ChatMessage`s into the bound chat session, and the
 * chat thread renders them — so there is no `onMessage`→`pushTranscript`
 * path here anymore. (An optimistic user bubble + token streaming are
 * explicit follow-ups, not part of this wave.)
 *
 * Lifecycle: the effect calls `startSession({ agentId })` once we have
 * a non-null agentId AND a non-null sessionId (the latter signals our
 * BE pre-warm succeeded). On unmount we call `endSession()` so the
 * SDK closes its WebRTC stream cleanly.
 *
 * MUST be used inside a `<ConversationProvider>`.
 */

import { useConversation } from '@elevenlabs/react';
import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useVoiceSession } from './useVoiceSession';

export interface UseElevenLabsConvAIReturn {
  status: ReturnType<typeof useConversation>['status'];
  mode: ReturnType<typeof useConversation>['mode'];
  isSpeaking: boolean;
  isListening: boolean;
  isMuted: boolean;
  setMuted: (muted: boolean) => void;
  endConversation: () => void;
}

/**
 * Drives the ElevenLabs SDK from our voice-session store. Returns a small
 * slice of the SDK's state for the UI to read (mode indicators, mute
 * toggle in the controls bar).
 */
export function useElevenLabsConvAI(
  sessionId: string | null,
  agentId: string | null,
): UseElevenLabsConvAIReturn {
  const conversation = useConversation({
    onError: (err: unknown) => {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'ElevenLabs conversation error';
      // eslint-disable-next-line no-console -- surface SDK errors during smoke
      console.error('[voice] elevenlabs error:', err);
      // Don't yank the user out of an open session for a transient SDK error.
      // Phase 5 will replace this with a more granular toast/banner.
      // Use setState-with-callback so the status check + write happen
      // atomically — otherwise endSession/reset can transition between our
      // getState read and setState write, and we'd clobber a 'closing' or
      // 'idle' status back to 'error'.
      useVoiceSession.setState((prev) => {
        if (prev.status !== 'connecting') {
          return prev; // mid-session or already closing — leave alone
        }
        return { ...prev, status: 'error', error: msg };
      });
    },
  });

  // Track the agentId we started against so a swap (project change) tears
  // down the old conversation before opening the new one.
  const startedAgentRef = useRef<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useConversation returns a fresh object each render, but the SDK keeps its session in a Provider — identity changes don't matter for lifecycle. Adding it would tear down + restart on every parent re-render.
  useEffect(() => {
    if (!sessionId || !agentId) return undefined;
    if (startedAgentRef.current === agentId) return undefined;

    startedAgentRef.current = agentId;
    // Force WebSocket transport. WebRTC mode (the SDK default for voice)
    // times out on the SDP negotiation against ElevenLabs's LiveKit-1.9.0
    // SFU in some browser/network combinations; WebSocket transport has no
    // UDP/STUN/TURN dependency, so it works uniformly on Chrome, Firefox,
    // and mobile browsers.
    // `userId` is stamped into EL's per-conversation metadata so the EL
    // dashboard can filter / group sessions by user. We deliberately do
    // NOT pass `overrides.agent.firstMessage` here — ElevenLabs agents
    // reject overrides unless explicitly whitelisted in
    // platform_settings.overrides. The greeting is baked into the agent's
    // static `first_message` field by the BE provisioner instead.
    const lvdrUserId = useAuthStore.getState().user?.id ?? undefined;
    conversation.startSession({
      agentId,
      connectionType: 'websocket',
      ...(lvdrUserId ? { userId: lvdrUserId } : {}),
    });

    return () => {
      try {
        conversation.endSession();
      } catch {
        // SDK may have already torn down on its own; safe to ignore.
      }
      startedAgentRef.current = null;
    };
  }, [sessionId, agentId]);

  return {
    status: conversation.status,
    mode: conversation.mode,
    isSpeaking: conversation.isSpeaking,
    isListening: conversation.isListening,
    isMuted: conversation.isMuted,
    setMuted: conversation.setMuted,
    endConversation: conversation.endSession,
  };
}
